import {createApp} from "vue"
import { createPinia } from "pinia"
import App from "./App.vue"
import router from "./router"

import * as Sentry from "@sentry/vue";
import { datadogRum } from '@datadog/browser-rum';

import "./assets/main.scss"
import "./assets/colors.scss"
import axios from "axios"
import {useErrorStore} from "@/stores/error_store"
import {useAuthStore} from "@/stores/auth_store"
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import VueLazyload from 'vue-lazyload'

// Initialize Datadog RUM for observability
datadogRum.init({
  applicationId: 'd0c01b9d-4d46-45cf-8dc2-77be7d5410e3',
  clientToken: 'pub8591507ac47d6ed4d26e2f5b6acf414d',
  site: 'us5.datadoghq.com',
  service: 'dda-frontend',
  env: 'prod',
  version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  trackBfcacheViews: true,
  defaultPrivacyLevel: 'allow',
});

const app = createApp(App)
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia)
app.use(router)
app.component('VueDatePicker', VueDatePicker)
app.use(VueLazyload, {
  preLoad: 1.3,
  attempt: 2
});

app.mount("#app")

Sentry.init({
  app,
  dsn: "https://e16fb738ae1c62d4b68b031795d1eddc@o4507368243724288.ingest.us.sentry.io/4508477196009473",
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/staging.dda-backend\.com/, /^https:\/\/sandbox.dda-backend\.com/, /^https:\/\/dda-backend\.com/, /^https:\/\/live.dda-backend\.com/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

/**
 * Load JWT from Local Storage on Refresh.
 */
const PREFIX = 'iP4BLqnibqamRTI_'
let localAuthToken = localStorage.getItem(`${PREFIX}auth_token`)
let cookieExists = localAuthToken !== 'undefined' && localAuthToken !== null && localAuthToken !== undefined
if (cookieExists) {
  axios.defaults.headers.common["Authorization"] = 'Bearer ' + localAuthToken.replace(/^Bearer /, '')
  
  // Set Datadog user from stored auth info
  try {
    const storedUser = localStorage.getItem(`${PREFIX}user`);
    if (storedUser) {
      const user = JSON.parse(storedUser);
      datadogRum.setUser({
        id: user.id?.toString() || 'unknown',
        name: user.name || user.email || 'Unknown User',
        email: user.email || '',
      });
    }
  } catch (e) {
    console.warn('Could not set Datadog user:', e);
  }
}

/*
 * Intercept Axios responses to indicate user login has expired.
 * https://axios-http.com/docs/interceptors
 */
const errorStore = useErrorStore()
const logoutMessage = errorStore.getLoggedOutMessage
axios.interceptors.response.use(function (response) {
  // Any status code that lie within the range of 2xx cause this function to trigger
  // Do something with response data
  return response
}, function (error) {
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  // Do something with response error
  if (error?.response?.status === 401) {
    errorStore.setError('Login Expired', logoutMessage, 'error', true)
  }
  return Promise.reject(error)
})

// NOTE: we could allow a last save, but given this currently works on the backend at 11:59pm, we shouldn't run into this much/at all.
errorStore.$subscribe(confirmNotLoggedIn)
function confirmNotLoggedIn() {
  if (errorStore.getMessage === logoutMessage && errorStore.getClearedStatus) {
    const authStore = useAuthStore()
    authStore.logoutUser()
  }
}

// Load Google Maps API
// Create a new Promise to load the Google Maps API
const loadGoogleMapsAPI = new Promise((resolve, reject) => {
  // Check if Google Maps API is already loaded
  if (window.google && window.google.maps) {
    resolve();
    return;
  }
  
  // Create a global callback function for the Google Maps API to call when loaded
  window.initGoogleMapsCallback = function() {
    resolve();
    // Clean up the global callback
    delete window.initGoogleMapsCallback;
  };
  
  // Create a new script element
  const script = document.createElement('script');
  
  // Set the source URL for the Google Maps API, including the API key, required libraries, and callback
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_ENV_API_GMAP}&callback=initGoogleMapsCallback&libraries=marker,places,geometry,drawing&v=weekly`;
  
  // Set the script to load asynchronously
  script.async = true;
  
  // Reject the promise if there is an error loading the script
  script.onerror = () => {
    reject(new Error('Failed to load Google Maps API'));
    // Clean up the global callback
    delete window.initGoogleMapsCallback;
  };
  
  // Append the script element to the document head to start loading the Google Maps API
  document.head.appendChild(script);
});

// Export for use in components
export { loadGoogleMapsAPI };